import config from '@/config';
import { extractControllersData } from '@/logic/controllers';
import { HOMEPAGE_BASE_PATH } from '@/logic/main/constants';
import { LogicContext } from '@/logic/main/types';
import {
  extractNavigationLinksFromNavComponent,
  getProductContext,
} from '@/logic/main/utils';
import { fetchPage } from '@/model/page';
import { getLocale } from '@/utils';
import { checkIfObjectsAreEqual } from '@/utils/lodash-alternatives/lodash-utils';

import { getErrorPageContent } from './getErrorPageContent';
import { initializePersonalizedBanners } from './initializePersonalizedBanners';

const returnErrorPage = (
  navigationLinks: LogicContext['navigationLinks'],
  trackingData: LogicContext['trackingData'] | {},
  segments: LogicContext['segments']
) => {
  const errorPage = getErrorPageContent();
  return {
    controllersData: {},
    navigationLinks,
    sidebarLayout: false,
    updates: [
      {
        type: 'initLayout',
        data: errorPage,
      },
    ],
    trackingData,
    noScroll: false,
    segments,
    isErrorPage: true,
  };
};

export const pageIdToLayoutChange = async (
  context: LogicContext,
  event: { reload_layout?: boolean; noScroll?: boolean }
) => {
  let { trackingData = {}, seo = {} } = context;
  const { segments = [], navigationLinks = [] } = context;
  if (context.isErrorPage)
    return {
      controllersData: {},
      navigationLinks: context.navigationLinks,
      sidebarLayout: false,
      updates: [],
      trackingData,
      noScroll: false,
      segments,
      isErrorPage: true,
    };

  const product = getProductContext();
  if (!product) return returnErrorPage(navigationLinks, trackingData, segments);

  let updates = [];
  const mainLayoutPath = context.mainLayoutPath;

  const shouldRefetchLayout =
    context.initialRequest || event.reload_layout || context.isErrorPage;

  const newData = await fetchPage({
    title: product,
    path:
      context.currentPagePath.replace(config.base_path || '', '') ||
      HOMEPAGE_BASE_PATH,
    ...(shouldRefetchLayout && {
      include_layout: true,
    }),
    locale: getLocale(config),
    viewer_country: config.viewer_country,
    viewer_region: config.viewer_region,
    logged_in: context.user.loggedIn,
    base_path: config.base_path,
    accountID: context.user.userAccountNumber,
    device_type: context.deviceInfo.deviceType,
    experimentUserId: context.tealiumUserId,
  });

  if (!newData || !newData.page) {
    return returnErrorPage(navigationLinks, trackingData, segments);
  }

  if (newData.trackingInfo) {
    trackingData = newData.trackingInfo;
  }

  const componentsMap = newData.page.renderManifest.components;
  const pageMainTitle = newData.page.trackingInfo?.SiteSubSection;

  const isSidebarInLayout = !!Object.entries(componentsMap).find(
    ([key, values]) => {
      const desktopSidebarMode =
        values.children &&
        values.children.includes('SearchModal') &&
        context.deviceInfo.deviceType === 'desktop' &&
        !config.staticComponents.Search?.desktopSidebarMode;
      return key === 'Modal' && values.children.length && !desktopSidebarMode;
    }
  );

  const videoComponentOnPage = Object.values(componentsMap).find(value => {
    return value.type === 'VideoModal' && value.properties.visible;
  });

  const searchBarComponentOnPage = Object.values(componentsMap).find(value => {
    return value.type === 'SearchBar';
  });

  const searchHeaderComponentOnPage = Object.values(componentsMap).find(
    value => {
      return value.type === 'SearchHeader';
    }
  );

  const idlePopupComponentOnPage = Object.values(componentsMap).find(value => {
    return value.type === 'IdlePopup';
  });

  const floatingIconComponentOnPage = Object.values(componentsMap).find(
    value => {
      return value.type === 'FloatingIcon';
    }
  );

  const jackpotBetWidgetComponentOnPage = Object.values(componentsMap).find(
    value => {
      return value.type === 'JackpotBetWidget';
    }
  );

  Object.entries(componentsMap).forEach(([key, component]: [any, any]) => {
    if (component.type !== 'Category') {
      updates.push(
        {
          type: 'setComponentChildren',
          data: {
            path: `components.${key}.children`,
            value: component.children || {},
          },
        },
        {
          type: 'setLayoutProperty',
          data: {
            path: `components.${key}.properties`,
            value: { ...component.properties, pageTitle: pageMainTitle },
          },
        }
      );
    }
  });

  if (context.initialRequest)
    updates.push({
      type: 'initLayout',
      data: { root: 'Layout', components: componentsMap },
    });

  updates.push({
    type: 'setComponentsMap',
    data: { value: componentsMap },
  });

  const pageTrackingData = newData.trackingInfo || {};
  if (context.changePageAction === 'VIEW_ALL') {
    const lastPage =
      context.previousPageArray[
        Math.max(context.previousPageArray.length - 1, 0)
      ]?.trackingData;

    Object.assign(pageTrackingData, {
      SiteSubSection:
        lastPage?.SiteSubSection ||
        lastPage?.SiteSectionCategory ||
        pageTrackingData.SiteSubSection ||
        null,
      SiteSectionCategory:
        pageTrackingData.SiteSectionCategory || pageTrackingData.SiteSubSection,
    });
  }

  if (
    isSidebarInLayout ||
    mainLayoutPath.includes(config.functionalities.search.path)
  ) {
    Object.assign(pageTrackingData, {
      SiteSectionCategory:
        context.trackingData?.page?.SiteSectionCategory ||
        pageTrackingData['SiteSectionCategory'] ||
        'Featured',
    });
  }
  Object.assign(seo, { page: newData.page.seo });

  const {
    gameLaunchStrategies = [],
    gameCloseStrategies = [],
    personalizedBanners = [],
  } = newData.page.renderManifest;
  Object.assign(trackingData, { page: pageTrackingData });
  const componentsNavigationLinks = extractNavigationLinksFromNavComponent(
    componentsMap.StaticNavigation,
    context.currentScreenSize
  );

  const personalizedBannersChanged: any = [];

  personalizedBanners?.forEach(pB => {
    const wasBefore = context.personalizedBanners?.find(pagePb =>
      checkIfObjectsAreEqual(pB, pagePb)
    );
    if (!wasBefore) personalizedBannersChanged.push(pB);
  });

  if (personalizedBannersChanged.length) {
    initializePersonalizedBanners(personalizedBannersChanged);
  } else if (
    context.user.loggedIn !== !!context.userStatusForCurrentPage ||
    (!isSidebarInLayout &&
      (context.deviceInfo?.deviceType === 'mobile' ||
        context.deviceInfo?.deviceType === 'tablet'))
  ) {
    initializePersonalizedBanners(context.personalizedBanners);
  }

  return {
    navigationLinks: navigationLinks.concat(componentsNavigationLinks),
    currentTablesIdList: newData.properties.liveDealerTables || [],
    updates: sortUpdates(updates),
    controllersData: extractControllersData(Object.entries(componentsMap)),
    mainLayoutPath: `${config.base_path || ''}${newData.page.id}`,
    isSidebarInLayout,
    isVideoEnabled: videoComponentOnPage,
    pageTitle:
      newData.page?.properties?.pageTitle || newData.properties.pageTitle,
    pageMainTitle,
    segments: newData.properties.segments,
    trackingData,
    personalizedBanners:
      isSidebarInLayout || searchHeaderComponentOnPage
        ? context.personalizedBanners
        : personalizedBanners,
    gameLaunchStrategies,
    gameCloseStrategies,
    seo,
    isErrorPage: ['NOT FOUND', 'NOT_FOUND'].includes(newData.properties.status),
    currentPagePath: context.mainLayoutPath || mainLayoutPath,
    noScroll: !!event.noScroll,
    searchBarComponent: context.searchBarComponent || {
      id: searchBarComponentOnPage?.properties.id || '',
    },
    jackpotBetWidgetComponent: {
      ...context.jackpotBetWidgetComponent,
      id: jackpotBetWidgetComponentOnPage?.properties.id,
    },
    idlePopupComponent: {
      ...context.idlePopupComponent,
      id:
        context.idlePopupComponent?.id ||
        idlePopupComponentOnPage?.properties?.id,
      longPauseTimeInterval:
        context.idlePopupComponent?.longPauseTimeInterval ||
        idlePopupComponentOnPage?.properties?.longPauseTimeInterval,
      shortPauseTimeInterval:
        context.idlePopupComponent?.shortPauseTimeInterval ||
        idlePopupComponentOnPage?.properties?.shortPauseTimeInterval,
    },
    floatingIconComponent: {
      id: floatingIconComponentOnPage?.properties.id,
    },
    userStatusForCurrentPage: context.user.loggedIn,
    registrationComponentMode: newData.properties?.newRegistrationMode,
  };
};

const updatesOrder: Record<string, number> = {
  initLayout: 0,
  setComponentsMap: 1,
  setComponentChildren: 2,
  setLayoutProperty: 3,
  showLayoutComponent: 4,
  hideLayoutComponent: 5,
};

function sortUpdates(arr: Array<{ type: string; data: any }>) {
  return arr.sort((a, b) => updatesOrder[a.type] - updatesOrder[b.type]);
}
